import React, { useEffect, useState, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast as toastify } from 'react-toastify';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AIChatProvider } from './context/AIChatContext';
import { PageVisibilityProvider, usePageVisibility, PageVisibilityContext } from './context/PageVisibilityContext';
import { SystemStatusProvider } from './context/SystemStatusContext';
import { connectWebSocket, closeWebSocket } from './services/ws';
import { toast } from 'react-hot-toast';
import NewNotificationAlert from './components/NewNotificationAlert';
import Notifications from './components/Notifications';
import MaintenanceMode from './components/MaintenanceMode';
import NavigationHandler from './components/NavigationHandler';
import NotificationNavigationHandler from './components/NotificationNavigationHandler';
import ProfileGuard, { ProfileCompletionBanner } from './components/ProfileGuard';
import CompleteProfilePage from './pages/CompleteProfilePage';


// Import NotificationManager để quản lý thông báo đẩy
import NotificationManager from './components/NotificationManager';


// Import Mobile Components
import MobileAppContainer from './components/mobile/MobileAppContainer';
import usePwaDisplayMode from './hooks/usePwaDisplayMode';
import './css/mobile-pwa.css';
import './utils/pwaDetection';

// Import GPS Debug utility for testing
import './utils/gpsDebug';

// Component để kiểm tra thông báo
const TestNotifications = () => {
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);

  const testWebSocket = async () => {
    try {
      const response = await fetch('/api/notifications/test-websocket', {
        credentials: 'include'
      });
      const data = await response.json();
      setResult(data);

      toast.success('Đã gửi thông báo test qua WebSocket');
    } catch (error) {
      console.error('Error testing WebSocket:', error);
      setResult({ error: error.message });
      toast.error('Lỗi khi gửi thông báo test');
    }
  };

  const testEvaluationNotification = async () => {
    try {
      const response = await fetch('/api/notifications/test-evaluation', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: message || 'Bạn có một đánh giá mới từ cấp trên',
          link: '/employee-evaluations'
        })
      });
      const data = await response.json();
      setResult(data);
      toast.success('Đã gửi thông báo đánh giá test');
    } catch (error) {
      console.error('Error sending evaluation notification:', error);
      setResult({ error: error.message });
      toast.error('Lỗi khi gửi thông báo đánh giá');
    }
  };

  const dispatchCustomEvent = () => {
    const event = new CustomEvent('notification_received', {
      detail: {
        notification: {
          _id: `test-${Date.now()}`,
          message: message || 'Thông báo test từ sự kiện tùy chỉnh',
          type: 'EVALUATION',
          icon: 'fas fa-clipboard-check',
          link: '/employee-evaluations',
          isRead: false,
          createdAt: new Date()
        }
      }
    });
    window.dispatchEvent(event);
    setResult({ success: true, message: 'Đã gửi sự kiện notification_received' });
    toast.success('Đã gửi sự kiện notification_received');
  };

  return (
    <div className="container mt-4">
      <h2>Kiểm tra thông báo</h2>
      <div className="card mb-4">
        <div className="card-body">
          <div className="mb-3">
            <label htmlFor="message" className="form-label">Nội dung thông báo</label>
            <input
              type="text"
              className="form-control"
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Nhập nội dung thông báo"
            />
          </div>

          <div className="d-flex gap-2 mb-3">
            <button className="btn btn-primary" onClick={testWebSocket}>
              Kiểm tra WebSocket
            </button>
            <button className="btn btn-success" onClick={testEvaluationNotification}>
              Gửi thông báo đánh giá
            </button>
            <button className="btn btn-info" onClick={dispatchCustomEvent}>
              Gửi sự kiện tùy chỉnh
            </button>
          </div>

          {result && (
            <div className="mt-3">
              <h5>Kết quả:</h5>
              <pre className="bg-light p-3 rounded">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Các component cần thiết ngay lập tức
import Navbar from './components/Navbar';
import HomeButton from './components/HomeButton';

import Breadcrumbs from './components/Breadcrumbs';
import WebSocketTracker from './components/WebSocketTracker';
import TopBrandBar, { BRAND_BAR_HEIGHT } from './components/TopBrandBar';

// Lazy load các component không cần thiết ngay lập tức
const ContractPage = lazy(() => import('./pages/ContractPage'));
const LaborContractForm = lazy(() => import('./components/contracts/LaborContractForm'));
const AIChatbot = lazy(() => import('./components/hr/HRChatbot'));
const AIChatPage = lazy(() => import('./pages/AIChatPage'));
const UploadsRedirect = lazy(() => import('./components/UploadsRedirect'));
const AssetManagement = lazy(() => import('./components/AssetManagement'));
const SuperAdminUserManagement = lazy(() => import('./pages/SuperAdminUserManagement'));

// Các trang cần thiết ngay lập tức (không lazy load)
import Login from './pages/Login';
import WelcomePage from './pages/WelcomePage';

// Lazy load các trang khác
const Register = lazy(() => import('./pages/Register'));
const RequestForm = lazy(() => import('./pages/RequestForm'));
const ApprovalDashboard = lazy(() => import('./pages/ApprovalDashboard'));
const ReturnForm = lazy(() => import('./pages/ReturnForm'));
const IncidentForm = lazy(() => import('./pages/IncidentForm'));
const ReportPage = lazy(() => import('./pages/ReportPage'));
const AddVehicle = lazy(() => import('./pages/AddVehicle'));
const IncidentReports = lazy(() => import('./pages/IncidentReports'));
const SupportFeedbackForm = lazy(() => import('./pages/SupportFeedbackForm'));
const SupportRequestsAdmin = lazy(() => import('./pages/SupportRequestsAdmin'));
const SupportRequestsUser = lazy(() => import('./pages/SupportRequestsUser'));
const HumanResources = lazy(() => import('./pages/HumanResources'));
const VehicleManagement = lazy(() => import('./pages/VehicleManagement'));
const WorkReports = lazy(() => import('./pages/WorkReports'));

const CreateUser = lazy(() => import('./pages/CreateUser'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const ReturnReports = lazy(() => import('./pages/ReturnReports'));
const Reception = lazy(() => import('./pages/Reception'));
const AdminWorkManagement = lazy(() => import('./pages/AdminWorkManagement'));
const UserGuide = lazy(() => import('./pages/UserGuide'));
const AllNotifications = lazy(() => import('./pages/AllNotifications'));
const DriverProfiles = lazy(() => import('./pages/DriverProfiles'));
const DriverSchedulePage = lazy(() => import('./pages/DriverSchedulePage'));
const AllDriverSchedulesPage = lazy(() => import('./pages/AllDriverSchedulesPage'));
const PickupSchedulePage = lazy(() => import('./pages/PickupSchedulePage'));
const LeaveRequestForm = lazy(() => import('./pages/LeaveRequestForm'));
const LeaveRequests = lazy(() => import('./pages/LeaveRequest'));
const AdminDriverManagementPage = lazy(() => import('./pages/AdminDriverManagementPage'));
const ActivityLogManagement = lazy(() => import('./pages/ActivityLogManagement'));
const FuelManagement = lazy(() => import('./pages/FuelManagement'));
const AdminNotificationPage = lazy(() => import('./pages/AdminNotificationPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const AdminAnnouncementsPage = lazy(() => import('./pages/AdminAnnouncementsPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const TodoListPage = lazy(() => import('./pages/TodoListPage'));
const CompanyDocumentsPage = lazy(() => import('./pages/CompanyDocumentsPage'));
const EmployeeEvaluationPage = lazy(() => import('./pages/EmployeeEvaluationPage'));
const PageVisibilityManagement = lazy(() => import('./pages/PageVisibilityManagement'));
const SystemStatusManagement = lazy(() => import('./pages/SystemStatusManagement'));
const HealthCheckDashboard = lazy(() => import('./pages/HealthCheckDashboard'));
const AuditTrailDashboard = lazy(() => import('./pages/AuditTrailDashboard'));
const CronDashboard = lazy(() => import('./pages/CronDashboard'));
const BackupManager = lazy(() => import('./pages/BackupManager'));
const ExecutiveDashboard = lazy(() => import('./pages/ExecutiveDashboard'));
const OnlineUsersPage = lazy(() => import('./pages/admin/OnlineUsersPage'));
const PushNotificationManager = lazy(() => import('./pages/PushNotificationManager'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
// Fallback for the old path
const NotificationSettingsPageFallback = lazy(() => import('./pages/NotificationSettingsPage'));

// Recruitment Pages
const RecruitmentDashboardPage = lazy(() => import('./pages/recruitment/RecruitmentDashboardPage'));
const RecruitmentRequestsPage = lazy(() => import('./pages/recruitment/RecruitmentRequestsPage'));
const CandidatesPage = lazy(() => import('./pages/recruitment/CandidatesPage'));

const InterviewsPage = lazy(() => import('./pages/recruitment/InterviewsPage'));
const TestRecruitmentAPI = lazy(() => import('./pages/recruitment/TestRecruitmentAPI'));

// PDF Management
const PDFManagement = lazy(() => import('./pages/PDFManagement'));

// Attendance Management
const AttendanceManagement = lazy(() => import('./pages/AttendanceManagement'));
// Performance Management
const PerformanceManagement = lazy(() => import('./pages/PerformanceManagement'));
const LinkManagement = lazy(() => import('./pages/LinkManagement'));

const ProbationaryManagement = lazy(() => import('./pages/ProbationaryManagement'));
const SoftwareRatingManagement = lazy(() => import('./pages/SoftwareRatingManagement'));
const BenefitsManagement = lazy(() => import('./pages/BenefitsManagement'));
const SalaryManagement = lazy(() => import('./pages/SalaryManagement'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const VehicleQRManager = lazy(() => import('./pages/VehicleQRManager'));
const PublicVehicleInfo = lazy(() => import('./pages/PublicVehicleInfo'));
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './css/custom.css';
import './css/custom-toast.css';
import './css/global-filter.css';

const getStoredUser = () => {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) return null;

  try {
    const parsedUser = JSON.parse(storedUser);
    if (parsedUser && typeof parsedUser === 'object') {
      return parsedUser;
    }
  } catch (error) {
    console.error("Failed to parse user from localStorage", error);
  }

  localStorage.removeItem('user');
  return null;
};

const getStoredAuthToken = () => {
  return localStorage.getItem('authToken') || localStorage.getItem('token') || null;
};

const hasValidSession = (user) => {
  const authToken = getStoredAuthToken();
  return Boolean(authToken && user && (user.id || user._id || user.username));
};

const PrivateRoute = ({ component: Component }) => {
  const isAuthenticated = hasValidSession(getStoredUser());
  return isAuthenticated ? <Component /> : <Navigate to="/login" />;
};

const AppContent = () => {
  const { loading: authLoading } = useAuth();
  const { isPwaExperience } = usePwaDisplayMode();
  const toastTopOffset = `${(isPwaExperience ? 10 : 75) + BRAND_BAR_HEIGHT}px`;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [latestNotification, setLatestNotification] = useState(null);

  // Make toast function globally available for API interceptor
  useEffect(() => {
    window.showToast = (type, message) => {
      switch (type) {
        case 'success':
          toastify.success(message);
          break;
        case 'error':
          toastify.error(message);
          break;
        case 'warning':
          toastify.warning(message);
          break;
        case 'info':
          toastify.info(message);
          break;
        default:
          toastify(message);
      }
    };

    // Cleanup
    return () => {
      delete window.showToast;
    };
  }, []);

  // Sử dụng state để lưu thông tin user
  const [user, setUser] = useState(() => getStoredUser());
  const location = useLocation();
  const isAuthenticated = hasValidSession(user);
  const isAuthRoute = location.pathname === '/login' || location.pathname === '/register' || location.pathname.startsWith('/vehicle-qr/');
  const showAuthenticatedShell = isAuthenticated && !isAuthRoute;

  // Lắng nghe sự kiện thay đổi trong localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      setUser(getStoredUser());
    };

    // Lắng nghe sự kiện authChange từ AuthContext
    window.addEventListener('authChange', handleStorageChange);

    // Lắng nghe sự kiện storage từ localStorage
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('authChange', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // State to track WebSocket connection status
  const [wsConnected, setWsConnected] = useState(false);



  useEffect(() => {
    const handleNotificationEvent = (event) => {
      if (event.detail && event.detail.notification) {
        setLatestNotification(event.detail.notification);
      }
    };
    const handleWsConnected = () => {
      console.log('[App] WebSocket connected');
      setWsConnected(true);
    };
    const handleWsDisconnected = () => {
      console.log('[App] WebSocket disconnected');
      setWsConnected(false);
    };
    // Add event listeners for WebSocket connection status
    window.addEventListener('ws_connected', handleWsConnected);
    window.addEventListener('ws_disconnected', handleWsDisconnected);
    window.addEventListener('notification_received', handleNotificationEvent);

    if (user?.role && !wsConnected) {
      // Trong backend, khi đăng nhập, nó trả về id: user._id
      const userId = user.id || user._id;
      console.log('Connecting WebSocket with userId:', userId);
      if (!userId) {
        console.error('User ID is undefined or null. User object:', user);
      }
      connectWebSocket({ userId: userId, role: user.role }, (data) => {
        // Show browser notification if allowed
        if (data.message && Notification.permission === 'granted') {
          const notification = new Notification('Thông báo mới', {
            body: data.message,
            icon: '/favicon.ico'
          });
          // Close notification after 5 seconds
          const notificationTimer = setTimeout(() => notification.close(), 5000);
          // Store timer for cleanup
          return () => clearTimeout(notificationTimer);
        }
        // Add to notifications state
        setNotifications((prev) => [...prev, data]);
        // Set latest notification for the alert component
        if (data.notification) {
          setLatestNotification(data.notification);
        } else if (data.type === 'notification') {
          setLatestNotification(data);
        }
      });
    }
    return () => {
      if (wsConnected) {
        closeWebSocket();
      }
      window.removeEventListener('notification_received', handleNotificationEvent);
      window.removeEventListener('ws_connected', handleWsConnected);
      window.removeEventListener('ws_disconnected', handleWsDisconnected);
    };
  }, [user, wsConnected]);

  // Simulate loading time and then set loading to false
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500); // 1.5 seconds loading time

    return () => clearTimeout(timer);
  }, []);

  const protectedRoute = (Component, requireRole) => {
    // Create a wrapper component to use hooks
    const PageVisibilityCheck = () => {
      // Get current path
      const currentPath = window.location.pathname;

      // Use the custom hook for page visibility
      const { isPageVisible, isPageInMaintenance, getMaintenanceMessage, loading } = usePageVisibility();

      // Check authentication using current state
      const currentToken = getStoredAuthToken();
      let currentUser = null;
      try {
        const userStr = localStorage.getItem('user');
        currentUser = userStr ? JSON.parse(userStr) : null;
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
      }

      // If not authenticated, redirect to login
      if (!currentToken) {
        console.log('No active session found, redirecting to login');
        return <Navigate to="/login" />;
      }

      console.log('Auth check passed:', { hasSession: !!currentToken, currentUser });

      // If role is required and user doesn't have it, redirect to home
      if (requireRole && currentUser?.role !== requireRole) return <Navigate to="/" />;

      // If loading visibility data, show loading spinner
      if (loading) {
        return <div className="loading-container"><div className="spinner"></div><p>Đang tải...</p></div>;
      }

      // SUPER_ADMIN can see all pages regardless of visibility settings
      // But we still want to check maintenance mode for them
      if (currentUser?.role === 'SUPER_ADMIN') {
        // Check if page is in maintenance mode
        const inMaintenance = isPageInMaintenance(currentPath);

        // If page is in maintenance mode, show maintenance page with bypass option
        if (inMaintenance) {
          const message = getMaintenanceMessage(currentPath);
          console.log(`[SUPER_ADMIN] Showing maintenance page for ${currentPath}`);
          return <MaintenanceMode message={message} />;
        }

        return <Component user={currentUser} />;
      }

      // Check if page is visible
      const visible = isPageVisible(currentPath);

      // If page is not visible, redirect to home with message
      if (!visible) {
        toast.error('Trang này hiện không khả dụng');
        return <Navigate to="/" />;
      }

      // Check if page is in maintenance mode
      const inMaintenance = isPageInMaintenance(currentPath);

      // If page is in maintenance mode, show maintenance page
      if (inMaintenance) {
        const message = getMaintenanceMessage(currentPath);
        return <MaintenanceMode message={message} />;
      }

      // If all checks pass, render the component
      return <Component user={currentUser} />;
    };

    return <PageVisibilityCheck />;
  };

  // Hàm bảo vệ route cho nhiều vai trò
  const protectedRouteMultiRole = (Component, allowedRoles = []) => {
    // Create a wrapper component to use hooks
    const MultiRoleVisibilityCheck = () => {
      // Get current path
      const currentPath = window.location.pathname;

      // Use the custom hook for page visibility
      const { isPageVisible, isPageInMaintenance, getMaintenanceMessage, loading } = usePageVisibility();

      // Check authentication using current state
      const currentToken = getStoredAuthToken();
      let currentUser = null;
      try {
        const userStr = localStorage.getItem('user');
        currentUser = userStr ? JSON.parse(userStr) : null;
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
      }

      // If not authenticated, redirect to login
      if (!currentToken) return <Navigate to="/login" />;

      // Check if user exists
      if (!currentUser) {
        toast.error('Phiên đăng nhập không hợp lệ, vui lòng đăng nhập lại.');
        return <Navigate to="/login" />;
      }

      // Check if user has allowed role
      if (allowedRoles.length > 0 && !allowedRoles.includes(currentUser?.role)) {
        toast.error('Bạn không có quyền truy cập trang này.');
        return <Navigate to="/" />;
      }

      // If loading visibility data, show loading spinner
      if (loading) {
        return <div className="loading-container"><div className="spinner"></div><p>Đang tải...</p></div>;
      }

      // SUPER_ADMIN can see all pages regardless of visibility settings
      // But we still want to check maintenance mode for them
      if (currentUser?.role === 'SUPER_ADMIN') {
        // Check if page is in maintenance mode
        const inMaintenance = isPageInMaintenance(currentPath);

        // If page is in maintenance mode, show maintenance page with bypass option
        if (inMaintenance) {
          const message = getMaintenanceMessage(currentPath);
          console.log(`[SUPER_ADMIN] Showing maintenance page for ${currentPath}`);
          return <MaintenanceMode message={message} />;
        }

        return <Component />;
      }

      // Check if page is visible
      const visible = isPageVisible(currentPath);

      // If page is not visible, redirect to home with message
      if (!visible) {
        toast.error('Trang này hiện không khả dụng');
        return <Navigate to="/" />;
      }

      // Check if page is in maintenance mode
      const inMaintenance = isPageInMaintenance(currentPath);

      // If page is in maintenance mode, show maintenance page
      if (inMaintenance) {
        const message = getMaintenanceMessage(currentPath);
        return <MaintenanceMode message={message} />;
      }

      // If all checks pass, render the component
      return <Component />;
    };

    return <MultiRoleVisibilityCheck />;
  };




  return (
    <PageVisibilityProvider>
      <SystemStatusProvider>
            {authLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <h2>Đang tải ứng dụng...</h2>
              </div>
            ) : (
              <div className="app-wrapper tailwind-styles">

                {/* Mobile Container wraps everything for mobile experience */}
                <MobileAppContainer>
                  {showAuthenticatedShell && <TopBrandBar />}
                  {showAuthenticatedShell && <Navbar />}

                  {/* Profile Completion Banner */}
                  {showAuthenticatedShell && <ProfileCompletionBanner />}

                  {/* WebSocketTracker for monitoring user activity */}
                  {showAuthenticatedShell && <WebSocketTracker />}

                  {/* NotificationManager for PWA push notifications */}
                  {showAuthenticatedShell && <NotificationManager />}

                  {latestNotification && (
                    <NewNotificationAlert
                      notification={latestNotification}
                      onClose={() => setLatestNotification(null)}
                    />
                  )}

                  {showAuthenticatedShell && <HomeButton />}

                  {/* AI Chatbot toàn cục — hiện với tất cả user đã đăng nhập */}
                  {showAuthenticatedShell && (
                    <Suspense fallback={null}>
                      <AIChatbot />
                    </Suspense>
                  )}

                  {/* Component hiển thị thông báo dạng toast */}
                  {showAuthenticatedShell && <Notifications />}

                  <ToastContainer position="top-center" autoClose={2000} pauseOnHover style={{ top: toastTopOffset }} />


                  <div className="container tailwind-styles" style={{ flex: '1 0 auto', paddingTop: isPwaExperience ? '0px' : '15px' }}>
                    {showAuthenticatedShell && <Breadcrumbs />}
                    <Suspense fallback={<div className="loading-container"><div className="spinner"></div><p>Đang tải...</p></div>}>
                      <MaintenanceMode>
                        <NavigationHandler />
                        <NotificationNavigationHandler />
                        <Routes>
                          <Route path="/vehicle-qr/:vehicleId" element={<PublicVehicleInfo />} />
                          <Route path="/uploads/*" element={<UploadsRedirect />} />
                          <Route path="/" element={isAuthenticated ? <WelcomePage user={user} /> : <Navigate to="/login" />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/complete-profile" element={isAuthenticated ? <CompleteProfilePage /> : <Navigate to="/login" />} />
                          <Route
                            path="/register"
                            element={
                              isAuthenticated && user && user.role === 'SUPER_ADMIN' ? (
                                <Register />
                              ) : (
                                <Navigate to="/" />
                              )
                            }
                          />
                          {/* Các trang cần đăng nhập */}

                          {/* Các trang cần đăng nhập */}
                          <Route path="/request" element={protectedRoute(RequestForm)} />
                          <Route path="/approval" element={protectedRoute(ApprovalDashboard)} />
                          <Route path="/return" element={protectedRoute(ReturnForm)} />
                          <Route path="/incident" element={protectedRoute(IncidentForm)} />
                          <Route path="/incident-reports" element={protectedRoute(IncidentReports)} />
                          <Route path="/report" element={protectedRoute(ReportPage)} />
                          <Route path="/add-vehicle" element={protectedRoute(AddVehicle)} />
                          <Route path="/profile" element={protectedRoute(UserProfile)} />
                          <Route path="/return-reports" element={protectedRoute(ReturnReports)} />
                          <Route path="/human-resources" element={protectedRoute(HumanResources)} />
                          <Route path="/vehicle-management" element={protectedRoute(VehicleManagement)} />
                          <Route path="/vehicle-qr-management" element={protectedRouteMultiRole(VehicleQRManager, ['ADMIN', 'SUPER_ADMIN'])} />
                          <Route path="/work-journal" element={protectedRoute(WorkReports)} />
                          <Route path="/support" element={protectedRoute(SupportFeedbackForm)} />
                          <Route path="/support-admin" element={protectedRoute(SupportRequestsAdmin)} />
                          <Route path="/my-support-requests" element={protectedRoute(SupportRequestsUser)} />
                          <Route path="/asset-management" element={protectedRoute(AssetManagement)} />
                          <Route path="/superadmin" element={protectedRoute(SuperAdminUserManagement, 'SUPER_ADMIN')} />
                          <Route path="/create-user" element={protectedRoute(CreateUser, 'SUPER_ADMIN')} />
                          <Route path="/all-notifications" element={<PrivateRoute component={AllNotifications} />} />
                          <Route path="/test-notifications" element={<TestNotifications />} />
                          <Route path="/driver-profiles" element={protectedRoute(DriverProfiles)} />
                          <Route path="/driver-schedule" element={protectedRoute(DriverSchedulePage)} />
                          <Route path="/all-driver-schedules" element={protectedRouteMultiRole(AllDriverSchedulesPage, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_I_MANAGER', 'LEVEL_II_MANAGER', 'ACCOUNTANT', 'REPORT_MANAGER'])} />
                          <Route path="/pickup-schedules" element={protectedRouteMultiRole(PickupSchedulePage, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER'])} />
                          <Route path="/leave-request/create" element={protectedRoute(LeaveRequestForm)} />
                          <Route path="/leave-requests" element={protectedRoute(LeaveRequests)} />
                          <Route path="/activity-logs" element={protectedRoute(ActivityLogManagement, 'SUPER_ADMIN')} />
                          <Route path="/admin-notifications" element={protectedRoute(AdminNotificationPage, 'SUPER_ADMIN')} />

                          <Route path="/reception" element={<Reception />} />
                          <Route path="/guide" element={<UserGuide />} />
                          <Route path="/privacy-policy" element={protectedRoute(PrivacyPolicy)} />
                          <Route path="/terms" element={protectedRoute(TermsOfService)} />
                          <Route path="/admin-work-management" element={<AdminWorkManagement />} />
                          <Route path="/announcements" element={protectedRoute(AnnouncementsPage)} />
                          <Route path="/admin/announcements" element={protectedRoute(AdminAnnouncementsPage, 'SUPER_ADMIN')} />
                          <Route path="/todos" element={protectedRoute(TodoListPage)} />
                          <Route path="/admin/driver-management" element={protectedRouteMultiRole(AdminDriverManagementPage, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER'])} />
                          <Route path="/fuel-management" element={protectedRouteMultiRole(FuelManagement, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_I_MANAGER', 'LEVEL_II_MANAGER', 'ACCOUNTANT'])} />
                          <Route path="/company-documents" element={protectedRoute(CompanyDocumentsPage)} />
                          <Route path="/employee-evaluations" element={protectedRoute(EmployeeEvaluationPage)} />
                          <Route path="/employee-evaluations/:id" element={protectedRoute(EmployeeEvaluationPage)} />
                          <Route path="/page-visibility" element={protectedRouteMultiRole(PageVisibilityManagement, ['SUPER_ADMIN'])} />
                          <Route path="/system-status" element={protectedRouteMultiRole(SystemStatusManagement, ['SUPER_ADMIN'])} />
                          <Route path="/health-check" element={protectedRouteMultiRole(HealthCheckDashboard, ['SUPER_ADMIN'])} />
                          <Route path="/audit-trail" element={protectedRouteMultiRole(AuditTrailDashboard, ['SUPER_ADMIN'])} />
                          <Route path="/cron-dashboard" element={protectedRouteMultiRole(CronDashboard, ['SUPER_ADMIN'])} />
                          <Route path="/backup-manager" element={protectedRouteMultiRole(BackupManager, ['SUPER_ADMIN'])} />
                          <Route path="/executive-dashboard" element={protectedRouteMultiRole(ExecutiveDashboard, ['ADMIN', 'SUPER_ADMIN'])} />
                          <Route path="/online-users" element={protectedRouteMultiRole(OnlineUsersPage, ['SUPER_ADMIN'])} />
                          <Route path="/push-notifications" element={protectedRoute(PushNotificationManager)} />
                          <Route path="/notification-settings" element={protectedRoute(NotificationSettingsPage)} />
                          {/* Bổ sung các trang cần đăng nhập còn thiếu */}
                          <Route path="/contract" element={protectedRoute(ContractPage)} />
                          <Route path="/labor-contract" element={protectedRoute(LaborContractForm)} />
                          <Route path="/recruitment-dashboard" element={protectedRouteMultiRole(RecruitmentDashboardPage, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_I_MANAGER', 'LEVEL_II_MANAGER', 'REPORT_MANAGER'])} />
                          <Route path="/recruitment-requests" element={protectedRouteMultiRole(RecruitmentRequestsPage, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_I_MANAGER', 'LEVEL_II_MANAGER', 'REPORT_MANAGER'])} />
                          <Route path="/candidates" element={protectedRouteMultiRole(CandidatesPage, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_I_MANAGER', 'LEVEL_II_MANAGER', 'REPORT_MANAGER'])} />

                          <Route path="/interviews" element={protectedRouteMultiRole(InterviewsPage, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_I_MANAGER', 'LEVEL_II_MANAGER', 'REPORT_MANAGER'])} />
                          <Route path="/test-recruitment-api" element={protectedRoute(TestRecruitmentAPI)} />

                          {/* Attendance Management */}
                          <Route path="/attendance-management" element={protectedRouteMultiRole(AttendanceManagement, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER'])} />

                          {/* Performance Management */}
                          <Route path="/performance-management" element={protectedRouteMultiRole(PerformanceManagement, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER', 'LEVEL_I_MANAGER', 'REPORT_MANAGER', 'EMPLOYEE'])} />

                          {/* Link Management */}
                          <Route path="/link-management" element={protectedRouteMultiRole(LinkManagement, ['SUPER_ADMIN'])} />
                          <Route path="/probationary-management" element={protectedRouteMultiRole(ProbationaryManagement, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER', 'REPORT_MANAGER'])} />

                          {/* PDF Management */}
                          <Route path="/pdf-management" element={protectedRoute(PDFManagement)} />
                          <Route path="/ai-chat" element={protectedRoute(AIChatPage)} />

                          {/* Software Rating Management */}
                          <Route path="/software-rating-management" element={protectedRouteMultiRole(SoftwareRatingManagement, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER'])} />

                          {/* Benefits Management */}
                          <Route path="/benefits-management" element={protectedRouteMultiRole(BenefitsManagement, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER'])} />

                          {/* Salary Management */}
                          <Route path="/salary-management" element={protectedRouteMultiRole(SalaryManagement, ['ADMIN', 'SUPER_ADMIN', 'LEVEL_II_MANAGER', 'ACCOUNTANT'])} />

                          {/* Trang 404 cho các route không tồn tại */}
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </MaintenanceMode>
                    </Suspense>
                  </div>

                  {showAuthenticatedShell && !isPwaExperience && (
                    <footer className="page-footer">
                      <div className="footer-main">
                        <div className="footer-content">
                          <div className="footer-row">
                            {/* CỘT 1 — BRAND */}
                            <div className="footer-brand">
                              <div className="footer-logo">
                                <img src="/img/logo.png" alt="New Atlantic Logo" loading="lazy" />
                              </div>
                              <div className="company-name-container">
                                <p className="company-name-primary">CÔNG TY CỔ PHẦN THƯƠNG MẠI QUỐC TẾ</p>
                                <p className="company-name-secondary">TÂN ĐẠI TÂY DƯƠNG</p>
                              </div>
                              <div className="footer-brand-divider"></div>
                              <p className="footer-tagline">Đối tác tin cậy trong lĩnh vực thương mại quốc tế</p>
                              <div className="social-links">
                                <a href="https://www.facebook.com/aittandaitayduong" title="Facebook" className="social-icon social-icon--facebook">
                                  <i className="fab fa-facebook-f"></i>
                                </a>
                                <a href="#" title="LinkedIn" className="social-icon social-icon--linkedin">
                                  <i className="fab fa-linkedin-in"></i>
                                </a>
                              </div>
                            </div>

                            {/* CỘT 2 — VĂN PHÒNG */}
                            <div className="footer-offices">
                              <h4 className="footer-section-title">VĂN PHÒNG</h4>
                              <div className="office-item">
                                <span className="office-label">TP. HỒ CHÍ MINH</span>
                                <div className="office-address">
                                  <i className="fas fa-map-marker-alt"></i>
                                  <span>A60 Đường Phú Thuận, P. Phú Thuận, TP.HCM</span>
                                </div>
                              </div>
                              {/* <div className="office-item">
                                <span className="office-label">HÀ NỘI</span>
                                <div className="office-address">
                                  <i className="fas fa-map-marker-alt"></i>
                                  <span>85 Nguyễn Phong Sắc, P. Dịch Vọng Hậu, Q. Cầu Giấy, Hà Nội</span>
                                </div>
                              </div> */}
                            </div>

                            {/* CỘT 3 — LIÊN HỆ */}
                            <div className="footer-contact-col">
                              <h4 className="footer-section-title">LIÊN HỆ</h4>
                              <div className="contact-row">
                                <i className="fas fa-phone-alt"></i>
                                <a href="tel:+842838733686">+84 2838 733 686</a>
                              </div>
                              <div className="contact-row">
                                <i className="fas fa-envelope"></i>
                                <a href="mailto:contact@newatlantic.vn">contact@newatlantic.vn</a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="footer-bottom">
                        <div className="footer-bottom-content">
                          <span className="footer-copyright-text">© {new Date().getFullYear()} NewAtlantic. All rights reserved.</span>
                          <div className="footer-bottom-links">
                            <a href="/privacy-policy">Chính sách bảo mật</a>
                            <span className="footer-dot">·</span>
                            <a href="/terms">Điều khoản sử dụng</a>
                          </div>
                        </div>
                      </div>
                    </footer>
                  )}
                </MobileAppContainer>
              </div>
            )}
      </SystemStatusProvider>
    </PageVisibilityProvider>
  );
};

const App = () => (
  <Router>
    <AuthProvider>
      <AIChatProvider>
        <AppContent />
      </AIChatProvider>
    </AuthProvider>
  </Router>
);

export default App;
